import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Drawer,
  Grid,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CollapseCard from './CollapseCard';
import { deepClone } from '@mui/x-data-grid/utils/utils';
import CloseIcon from '@mui/icons-material/Close';
import useModal from '@tenant/utility/hooks/useModal';
import CronModal from '@tenant/core/App/Cron/CronModal';
import { cronRegex } from 'redux/slices/appInstance/constants/validationSchema';
import PropTypes from 'prop-types';

const generateRandomID = () => {
  return 'id-' + Date.now() + '-' + Math.floor(Math.random() * 10000);
};

const AddEnvironmentDrawer = ({
  visible,
  handleClose,
  appType,
  title,
  requiredEnviromentVariables,
  cron,
  readonly,
  setContainerApps = () => {},
}) => {
  const isCron = useMemo(() => appType === 3, [appType]);
  const [environments, setEnvironments] = useState([]);
  const [cronValue, setCronValue] = useState(cron);
  const [isChangeCron, setIsChangeCron] = useState(false);
  const { visible: showSetCron, onClose: onCloseSetCron } = useModal();

  useEffect(() => {
    setEnvironments(deepClone(requiredEnviromentVariables));
  }, [requiredEnviromentVariables]);

  const setFieldValue = (index, field, value) => {
    setEnvironments((prevEnvironments) =>
      prevEnvironments.map((env, i) =>
        i === index ? { ...env, [field]: value } : env,
      ),
    );
  };

  const deleteEnv = (env) => {
    setEnvironments((s) => s.filter((e) => e.id !== env.id));
  };

  const addNewRow = () => {
    setEnvironments((s) => [
      ...s,
      {
        id: 'new-item-' + generateRandomID(),
        key: '',
        value: '',
        isValid: true,
        isRequired: false,
      },
    ]);
  };

  const checkIsValidateCronRegex = (cronValue) => {
    return cronRegex.test(cronValue);
  };

  return (
    <Drawer anchor={'right'} open={visible} onClose={handleClose}>
      <Box sx={{ width: 700 }}>
        <Box
          sx={{
            height: 'calc(100vh - 55px)',
            position: 'sticky',
            p: 4,
            overflow: 'auto',
          }}
        >
          <Box
            mb={5}
            display={'flex'}
            justifyContent={'space-between'}
            alignItems={'center'}
          >
            <Typography variant='h3'>{title} </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {isCron && (
            <Box mt={4}>
              <Box component='p' color='text.primary' fontSize={14} mb={2}>
                Cron{' '}
                <Box component={'span'} color={'error.main'}>
                  *
                </Box>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    disabled={readonly}
                    type='string'
                    fullWidth
                    value={cronValue ?? ''}
                    error={
                      isChangeCron &&
                      (!cronValue || !checkIsValidateCronRegex(cronValue))
                    }
                    inputProps={{ maxLength: 255 }}
                    placeholder='E.g. * * * * * *'
                    helperText={
                      isChangeCron
                        ? !cronValue
                          ? 'Cron is required'
                          : !checkIsValidateCronRegex(cronValue)
                          ? 'Cron is invalid'
                          : ''
                        : ''
                    }
                    onChange={(e) => {
                      setCronValue(e.target.value);
                      setIsChangeCron(true);
                    }}
                  />
                  <Typography mt={2} variant='body2'>
                    Format: {'{minute} {hour} {day} {month} {day-of-week}'}
                  </Typography>
                </Grid>
              </Grid>

              <CronModal
                visible={showSetCron}
                handleClose={onCloseSetCron}
                handleSave={(value) => {
                  setCronValue(value);
                  onCloseSetCron();
                }}
              />
            </Box>
          )}

          <Box mt={4}>
            <CollapseCard
              readonly={readonly}
              label={`Environments`}
              environments={environments}
              setFieldValue={setFieldValue}
              setEnvironments={(value) => setEnvironments(value)}
              deleteEnv={deleteEnv}
              addNewRow={addNewRow}
            />
          </Box>
        </Box>

        {!readonly && (
          <Box
            sx={{
              p: 2,
              borderTop: '1px solid #ddd',
              textAlign: 'right',
              '& .MuiButton-root': {
                ml: 2,
                mr: 2,
              },
            }}
          >
            <Button onClick={handleClose}> Cancel </Button>
            <Button
              variant='contained'
              onClick={() => {
                const isValidCron = isCron
                  ? !!checkIsValidateCronRegex(cronValue)
                  : true;
                setContainerApps({
                  environments: environments.filter((e) => !!e.key),
                  cron: cronValue,
                  isValid: isValidCron,
                });
                handleClose();
              }}
            >
              Save
            </Button>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

AddEnvironmentDrawer.propTypes = {
  visible: PropTypes.bool,
  handleClose: PropTypes.func,
  appType: PropTypes.string,
  title: PropTypes.string,
  requiredEnviromentVariables: PropTypes.array,
  cron: PropTypes.bool,
  readonly: PropTypes.bool,
  setContainerApps: PropTypes.func,
};

export default React.memo(AddEnvironmentDrawer);
